import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutAdmin } from "../../store/thunks/authThunks";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const Navbar = ({ onMenuClick }) => {
  const dispatch = useDispatch();
  const { admin, isAuthenticated } = useSelector((state) => state.auth);
  const [envDropdownOpen, setEnvDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [currentEnv, setCurrentEnv] = useState("PROD");
  const [isChangingEnv, setIsChangingEnv] = useState(false);
  const envDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const handleLogout = () => {
    dispatch(logoutAdmin());
  };

  const verifyEnvironment = async (targetEnv, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/config/environment`);
        console.log(`Verification attempt ${i + 1}:`, response.data);
        if (response.data.environment === targetEnv) {
          return true;
        }
      } catch (error) {
        console.error(`Verification attempt ${i + 1} failed:`, error);
      }
    }
    return false;
  };

  const handleEnvironmentChange = async (env) => {
    try {
      setIsChangingEnv(true);
      const apiUrl = `${import.meta.env.VITE_API_URL}/api/config/environment`;
      
      // First verify that the environment change is possible
      const response = await axios.post(apiUrl, { environment: env });

      if (response.data.success) {
        setCurrentEnv(env);
        toast.success(`Environment changed to ${env}`);
        setEnvDropdownOpen(false);

        // Clear any cached data
        localStorage.removeItem('projectsCache');
        localStorage.removeItem('usersCache');
        sessionStorage.clear();

        // Add a small delay before reloading to ensure environment is properly set
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Verify the environment change was successful
        try {
          const verifyResponse = await axios.get(apiUrl);
          if (verifyResponse.data.environment !== env) {
            throw new Error('Environment verification failed');
          }
        } catch (verifyError) {
          console.error('Environment verification failed:', verifyError);
          // Continue with reload anyway as the initial change was successful
        }

        // Reload the page to apply the new environment
        console.log('Reloading page to apply new environment...');
        window.location.reload();
      } else {
        console.error('Failed to change environment:', response.data);
        toast.error("Failed to change environment");
      }
    } catch (error) {
      console.error("Error changing environment:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });
      
      // Show a more user-friendly error message
      if (error.response?.status === 503) {
        toast.warning("Environment change in progress. Please wait a moment and try again.");
      } else {
        toast.error(error.response?.data?.message || "Failed to change environment");
      }
      
      // Try to revert the UI state
      try {
        const currentEnvResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/config/environment`);
        if (currentEnvResponse.data.success) {
          setCurrentEnv(currentEnvResponse.data.environment);
        }
      } catch (revertError) {
        console.error('Failed to revert environment state:', revertError);
      }
    } finally {
      setIsChangingEnv(false);
    }
  };

  const toggleEnvDropdown = () => {
    setEnvDropdownOpen(!envDropdownOpen);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
    setEnvDropdownOpen(false);
  };

  // Fetch current environment on component mount
  useEffect(() => {
    const fetchCurrentEnvironment = async () => {
      try {
        console.log("Fetching current environment...");
        const apiUrl = `${import.meta.env.VITE_API_URL}/api/config/environment`;
        console.log(`API URL: ${apiUrl}`);

        const response = await axios.get(apiUrl);
        console.log("Environment response:", response.data);

        if (response.data.success) {
          setCurrentEnv(response.data.environment);
          console.log(
            `Current environment set to: ${response.data.environment}`
          );
        }
      } catch (error) {
        console.error("Error fetching current environment:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
    };

    fetchCurrentEnvironment();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        envDropdownRef.current &&
        !envDropdownRef.current.contains(event.target)
      ) {
        setEnvDropdownOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Use a ref to track if we've already set the environment for non-admin users
  const hasSetDefaultEnv = useRef(false);
  
  // Set default environment for non-admin users
  useEffect(() => {
    const setDefaultEnvironment = async () => {
      // Only run this effect when admin data is available and we haven't set the environment yet
      if (admin && admin.role !== "admin" && !hasSetDefaultEnv.current && currentEnv !== "PROD") {
        console.log("Setting default environment to PROD for non-admin user");
        hasSetDefaultEnv.current = true; // Set this first to prevent multiple calls
        
        try {
          // Check current environment from backend
          const apiUrl = `${import.meta.env.VITE_API_URL}/api/config/environment`;
          const response = await axios.get(apiUrl);
          
          // Only change to PROD if not already on PROD
          if (response.data.success && response.data.environment !== "PROD") {
            console.log("Current environment is not PROD, changing to PROD");
            
            // Make a direct API call instead of using handleEnvironmentChange
            // to avoid page reload and potential infinite loop
            await axios.post(apiUrl, { environment: "PROD" });
            setCurrentEnv("PROD");
          } else {
            console.log("Environment is already PROD, no change needed");
          }
        } catch (error) {
          console.error("Error setting default environment:", error);
        }
      }
    };
    
    setDefaultEnvironment();
  }, [admin, currentEnv]);

  return (
    <nav className="bg-gradient-to-r from-[#E6CCB2] to-[#F2D0A9] shadow-lg fixed top-0 left-0 w-full z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md text-[#2E1F27] hover:bg-[#B85C38] hover:text-white transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#B85C38]"
              aria-label="Open menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <Link
              to="/"
              className="ml-4 font-bold text-xl text-[#2E1F27] hover:text-[#B85C38] transition-colors duration-200 flex items-center"
            >
              <span className="mr-2">🌟</span>
              Icod Admin
            </Link>
          </div>

          <div className="flex items-center">
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <div className="relative" ref={envDropdownRef}>
                    {admin?.role === "admin" && (
                      <button
                        onClick={toggleEnvDropdown}
                        disabled={isChangingEnv}
                        className={`flex items-center px-3 py-2 rounded-md text-[#2E1F27] hover:bg-[#B85C38] hover:text-white transition-all duration-300 ${
                          isChangingEnv ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        aria-expanded={envDropdownOpen}
                        aria-haspopup="true"
                      >
                        <span className="mr-1">Environment: </span>
                        <span className="font-semibold mr-1">{currentEnv}</span>
                        {isChangingEnv ? (
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg
                            className={`h-4 w-4 transition-transform duration-200 ${
                              envDropdownOpen ? "rotate-180" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                    {envDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 transform transition-all duration-200 origin-top-right">
                        <button
                          onClick={() => handleEnvironmentChange("DEV")}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          Dev
                        </button>
                        <button
                          onClick={() => handleEnvironmentChange("UAT")}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          UAT
                        </button>
                        <button
                          onClick={() => handleEnvironmentChange("QA")}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          QA
                        </button>
                        <button
                          onClick={() => handleEnvironmentChange("PROD")}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          Prod
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
              {isAuthenticated ? (
                <>
                  <div className="relative" ref={profileDropdownRef}>
                    <button
                      onClick={toggleProfileDropdown}
                      className="flex items-center px-3 py-2 rounded-md text-[#2E1F27] hover:bg-[#B85C38] hover:text-white transition-all duration-300"
                      aria-expanded={profileDropdownOpen}
                      aria-haspopup="true"
                    >
                      <span className="mr-1">{admin?.fullName || "Admin"}</span>
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${
                          profileDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {profileDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 transform transition-all duration-200 origin-top-right">
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          <div className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              ></path>
                            </svg>
                            My Profile
                          </div>
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-[#F2D0A9] transition-colors duration-200"
                        >
                          <div className="flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              ></path>
                            </svg>
                            Logout
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-2 rounded-md text-[#2E1F27] hover:bg-[#B85C38] hover:text-white transition-all duration-300"
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                        ></path>
                      </svg>
                      Login
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
